import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faXmark } from "@fortawesome/free-solid-svg-icons";
import { BRAND } from "constants/brand";
import "./searchInput.scss";

const DEFAULT_DEBOUNCE_MS = 400;

/**
 * Shared search box used across the dashboard lists.
 *
 * - Debounces the `onSearch` callback (default 400ms) so we fire one request
 *   after the user stops typing instead of one per keystroke.
 * - Controlled internally and seeded once from `initialValue`, so the field
 *   never resets mid-typing when the parent list refetches.
 * - Shows a clear (×) button and a magnifying-glass affordance.
 *
 * @param {object} props
 * @param {string} [props.initialValue] Value to seed the field with on mount.
 * @param {(value: string) => void} props.onSearch Called (debounced) with the trimmed term.
 * @param {string} [props.placeholder] Placeholder text (RTL/Arabic).
 * @param {number} [props.minWidth] Minimum width of the input in px.
 * @param {number} [props.debounceMs] Debounce delay in ms.
 */
const SearchInput = ({
    initialValue = "",
    onSearch,
    placeholder = "بحث",
    minWidth = 250,
    debounceMs = DEFAULT_DEBOUNCE_MS,
}) => {
    const [value, setValue] = useState(initialValue);
    const isFirstRun = useRef(true);
    const onSearchRef = useRef(onSearch);

    // Keep the latest callback in a ref so the debounce effect depends only on
    // `value` and doesn't reset its timer when the parent passes a new inline
    // onSearch each render.
    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);

    useEffect(() => {
        // Skip the initial mount so we don't re-emit the value the parent
        // already has (e.g. the current URL/redux filter).
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return undefined;
        }
        const timer = setTimeout(() => onSearchRef.current(value.trim()), debounceMs);
        return () => clearTimeout(timer);
    }, [value, debounceMs]);

    return (
        <div className="search-input">
            <FontAwesomeIcon
                className="search-input__icon"
                icon={faMagnifyingGlass}
                color={BRAND}
            />
            <input
                type="search"
                className="form-control search-input__field"
                placeholder={placeholder}
                value={value}
                onChange={(event) => setValue(event.target.value)}
                style={{ minWidth: `${minWidth}px` }}
            />
            {value ? (
                <button
                    type="button"
                    className="search-input__clear"
                    aria-label="مسح البحث"
                    onClick={() => setValue("")}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            ) : null}
        </div>
    );
};

export default SearchInput;
